import { Dispatch, DispatchWithoutAction } from "react";
import { Form } from "react-final-form";
import { CheckIcon } from "lucide-react";

import { useTranslation } from "@/lib/i18n";
import { ParametersAbstractMapLayer } from "@/lib/api";
import Dialog from "@/components/dialog/dialog";
import DialogHeader from "@/components/dialog/dailog-header";
import dialogVariants from "@/components/dialog/dialog-variants.module.css";
import { TextField } from "@/features/shared/components";
import Button from "@/components/button/button";
import Stack from "@/components/stack/stack";
import DialogFooter from "@/components/dialog/dialog-footer";
import { ItemCardContent } from "@/features/widget-forms/forms/weather-model-map/components/item-card-content/item-card-content";
import { fmtSubtitle } from "@/features/widget-forms/forms/weather-model-map/modals/map-layer-formatters";

import styles from "./configure-layer-modal.module.css";

interface ConfigureLayerModalProps {
  layer: ParametersAbstractMapLayer | undefined;
  open: boolean;
  onSubmit: Dispatch<LayerDetails>;
  onCancel: DispatchWithoutAction;
  initialValues?: Partial<LayerDetails>;
}

export interface LayerDetails {
  label: string;
}

export function ConfigureLayerModal({
  open,
  layer,
  onSubmit,
  onCancel,
  initialValues,
}: ConfigureLayerModalProps) {
  const { t } = useTranslation();
  // After selecting a weather model chart layer, you must set a name override before it is added.

  return (
    <Dialog open={open}>
      <Dialog.Content className={dialogVariants.default}>
        <Form onSubmit={onSubmit}>
          {(formProps) => (
            <form onSubmit={formProps.handleSubmit}>
              <DialogHeader.Root>
                <DialogHeader.Title>
                  {t("admin.configure_layer_modal.title")}
                </DialogHeader.Title>
              </DialogHeader.Root>
              <h3 className={styles.header}>
                {t("admin.configure_layer_modal.preview_header")}
              </h3>
              {layer ? <LayerModalPreview layer={layer} /> : null}
              <h3 className={styles.header}>
                {t("admin.configure_layer_modal.settings_header")}
              </h3>
              <TextField
                name="label"
                label={t("admin.configure_layer_modal.label.label")}
                helperText={t("admin.configure_layer_modal.label.helper_text")}
                autoFocus
                required
                defaultValue={initialValues?.label}
                className={styles.textField}
              />
              <DialogFooter>
                <Stack flexDirection="row" justifyContent={"flex-end"} gap={8}>
                  <Button type="button" variant="solid-secondary" onClick={onCancel}>
                    {t("general.cancel")}
                  </Button>
                  <Button type="submit" variant="solid">
                    <CheckIcon />
                    {t("general.done")}
                  </Button>
                </Stack>
              </DialogFooter>
            </form>
          )}
        </Form>
      </Dialog.Content>
    </Dialog>
  );
}
function LayerModalPreview({ layer }: { layer: ParametersAbstractMapLayer }) {
  return (
    <div className={styles.layerPreview}>
      <ItemCardContent title={layer.label} subtitle={fmtSubtitle(layer.zoom_levels)} />
    </div>
  );
}
